import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { deliveryAPI, paymentAPI } from '../../services/api';

function DeliveryPaymentVerification() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [message, setMessage] = useState('Verifying your payment...');
  const [attempts, setAttempts] = useState(0);
  const attemptsRef = useRef(0);
  const maxAttempts = 10;

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      // Increment attempt counter
      attemptsRef.current += 1;
      setAttempts(attemptsRef.current);

      console.log(`Payment verification attempt ${attemptsRef.current} of ${maxAttempts}`);

      // First, check if webhook already updated payment status
      const response = await deliveryAPI.getOnboardingStatus();
      const deliveryPartner = response.data.data.deliveryPartner;

      // Check if payment is already marked as paid
      if (deliveryPartner && deliveryPartner.onboardingFee && deliveryPartner.onboardingFee.paid) {
        console.log('✅ Payment already verified by webhook');

        // Automatically submit for approval
        try {
          await deliveryAPI.submitForApproval();
          setStatus('success');
          setMessage('Payment verified! Your application has been submitted for admin approval.');
          setTimeout(() => {
            navigate('/delivery/onboarding');
          }, 2000);
        } catch (submitError) {
          console.error('Submit for approval error:', submitError);
          setStatus('success');
          setMessage('Payment verified! Please return to onboarding to complete your application.');
          setTimeout(() => {
            navigate('/delivery/onboarding');
          }, 2000);
        }
        return;
      }

      // If not activated yet and we have temp order ID, try manual verification
      if (deliveryPartner && deliveryPartner.tempCashfreeOrderId) {
        console.log(`🔍 Attempting manual verification for order: ${deliveryPartner.tempCashfreeOrderId}`);

        const verifyResponse = await paymentAPI.verifySubscriptionPayment({
          cashfreeOrderId: deliveryPartner.tempCashfreeOrderId
        });

        if (verifyResponse.data.success) {
          console.log('✅ Payment verified manually through Cashfree API');

          // Automatically submit for approval
          try {
            await deliveryAPI.submitForApproval();
            setStatus('success');
            setMessage('Payment successful! Your application has been submitted for admin approval.');
            setTimeout(() => {
              navigate('/delivery/onboarding');
            }, 2000);
          } catch (submitError) {
            console.error('Submit for approval error:', submitError);
            setStatus('success');
            setMessage('Payment successful! Please return to onboarding to complete your application.');
            setTimeout(() => {
              navigate('/delivery/onboarding');
            }, 2000);
          }
          return;
        } else {
          // Check if payment explicitly failed
          if (verifyResponse.data.data?.paymentStatus === 'FAILED') {
            setStatus('failed');
            setMessage('Payment failed. ' + (verifyResponse.data.data.message || 'Please try again or contact support.'));
            return;
          }

          // Payment not successful yet, retry if under max attempts
          if (attemptsRef.current < maxAttempts) {
            setMessage(`Verifying payment... Attempt ${attemptsRef.current} of ${maxAttempts}`);
            setTimeout(() => {
              verifyPayment();
            }, 2000);
          } else {
            setStatus('failed');
            setMessage('Payment verification is taking longer than expected. Please refresh the page or contact support if the amount was deducted.');
          }
        }
      } else {
        // No order ID found
        setStatus('failed');
        setMessage('Payment session not found. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);

      // Retry if under max attempts
      if (attemptsRef.current < maxAttempts) {
        setMessage(`Verifying payment... Attempt ${attemptsRef.current} of ${maxAttempts}`);
        setTimeout(() => {
          verifyPayment();
        }, 2000);
      } else {
        setStatus('failed');
        setMessage('Payment verification failed. Please refresh the page or contact support if the amount was deducted.');
      }
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)'
    }}>
      <div className="card fade-in" style={{ maxWidth: '600px', textAlign: 'center' }}>
        {/* Icon */}
        <div style={{
          width: '120px',
          height: '120px',
          background: status === 'success'
            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            : status === 'failed'
            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
            : 'var(--gradient-primary)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto var(--space-2xl)',
          fontSize: '4rem',
          animation: status === 'verifying' ? 'spin 2s linear infinite' : 'none'
        }}>
          {status === 'verifying' && '🔄'}
          {status === 'success' && '✅'}
          {status === 'failed' && '❌'}
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: 'var(--font-size-3xl)',
          fontWeight: '800',
          color: 'var(--gray-900)',
          marginBottom: 'var(--space-lg)'
        }}>
          {status === 'verifying' && 'Verifying Payment'}
          {status === 'success' && 'Payment Successful!'}
          {status === 'failed' && 'Verification Issue'}
        </h1>

        {/* Message */}
        <p style={{
          fontSize: 'var(--font-size-lg)',
          color: 'var(--gray-600)',
          marginBottom: 'var(--space-2xl)',
          lineHeight: '1.6'
        }}>
          {message}
        </p>

        {/* Progress indicator for verifying state */}
        {status === 'verifying' && (
          <div style={{
            marginBottom: 'var(--space-xl)'
          }}>
            <div style={{
              width: '100%',
              height: '4px',
              background: 'var(--gray-200)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(attempts / maxAttempts) * 100}%`,
                height: '100%',
                background: 'var(--gradient-primary)',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
            <p style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--gray-500)',
              marginTop: 'var(--space-sm)'
            }}>
              Attempt {attempts} of {maxAttempts}
            </p>
          </div>
        )}

        {/* Action buttons */}
        {status === 'failed' && (
          <div style={{
            display: 'flex',
            gap: 'var(--space-md)',
            justifyContent: 'center'
          }}>
            <button
              onClick={() => navigate('/delivery/onboarding')}
              className="btn btn-secondary"
            >
              Back to Onboarding
            </button>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary"
            >
              Try Again
            </button>
          </div>
        )}

        {status === 'success' && (
          <p style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--gray-500)'
          }}>
            Redirecting to onboarding...
          </p>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default DeliveryPaymentVerification;
